import { message, notification } from 'antd';
import { CheckCircleFilled } from '@ant-design/icons';
import { createElement } from 'react';
import i18n from '@/i18n';

interface SuccessOptions {
  /**
   * An i18n key, resolved when the toast fires.
   *
   * Prefer this over `title`. These callers are mostly `onSuccess` handlers in
   * `entities/<domain>/api/mutations.ts`, which are modules rather than components —
   * there is no `useTranslation()` to reach for, and a literal string there is
   * how ~126 success toasts ended up rendering in English no matter which
   * language the merchant had chosen. Resolving here keeps the lookup at toast
   * time, so it follows a language switched mid-session.
   */
  titleKey?: string;
  /** i18n key for the body line. Same reasoning as `titleKey`. */
  descriptionKey?: string;
  /** Interpolation values for `titleKey` / `descriptionKey`, e.g. `{ email }`. */
  params?: Record<string, unknown>;
  /** Literal title. Kept for callers with a genuinely dynamic string. */
  title?: string;
  description?: string;
}

/**
 * The success toast. CLAUDE.md names this the standard for mutation feedback
 * and 31 files use it — but 15 more were calling `message.success` or
 * `notification.success` directly, which meant the same event rendered two
 * ways with two default durations.
 *
 * `data` is normally the API response, so a backend-supplied title/message
 * wins. It also accepts a plain string for the successes that have no
 * response to speak of — a local copy-to-clipboard, a draft saved client-side
 * — which is what those 15 call sites needed and didn't have.
 */
export function handleSuccess(
  data: any | string,
  options?: SuccessOptions,
) {
  if (typeof data === 'string') {
    data = { title: data };
  }
  // Precedence is unchanged: a backend-supplied title still wins, because it
  // can carry specifics ours cannot. Worth knowing that this caps how much of
  // the toast surface the frontend can localise on its own — wherever the API
  // returns a title, translating it is the backend's job.
  const title =
    data?.title ??
    (options?.titleKey ? i18n.t(options.titleKey, options.params) : options?.title) ??
    i18n.t('success.saved');
  const description =
    data?.message ??
    (options?.descriptionKey ? i18n.t(options.descriptionKey, options.params) : options?.description) ??
    '';
  notification.success({
    message: title,
    description,
    duration: 4,
    icon: createElement(CheckCircleFilled, {
      'aria-hidden': true,
      style: { color: 'var(--color-success)' },
    }),
  });
}

/**
 * The lighter success toast — a strip, not a card.
 *
 * Deliberately distinct from `handleSuccess`: "URL copied" does not warrant a
 * titled notification card in the corner, and "Subscription upgraded" does.
 * Both go through helpers so the weight is a choice rather than an accident of
 * which AntD API the author reached for.
 *
 * Accepts an API response (a backend `message` wins) or a plain string.
 */
export function handleSuccessMessage(
  data: any | string,
  /**
   * The fallback when the response carries no message. Pass `{ key }` for an
   * i18n key — preferred, for the reason on `titleKey` — or a literal string
   * when it is genuinely dynamic.
   */
  fallback?: string | { key: string },
) {
  if (typeof data === 'string') return message.success(data);
  if (data?.message) return message.success(data.message);
  const resolved =
    typeof fallback === 'object' ? i18n.t(fallback.key) : fallback;
  message.success(resolved ?? i18n.t('success.saved'));
}
